import { useReduxState } from 'hooks';
import { RichTextValue } from 'components/common';
import { EditorBlockTranslationReference } from 'components/editor';
import { TextEditor } from 'components/editor/inputs';
import {
  EditorBlockConverter,
  EditorBlockCustomControls
} from 'components/editor/blocks';
import { EditorBlockTextHighlightControls } from '.';

import type { BlockText } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockText = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockText> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages } = useReduxState();

  const handleChange = (newContent: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            text: newContent
          }
        }
      }
    });
  };

  const { backgroundColor, borderColor, color, textSize } = data;

  return (
    <>
      <div
        className="content"
        style={
          (backgroundColor || borderColor) && color
            ? {
                backgroundColor: backgroundColor || 'transparent',
                border: borderColor ? `2px solid ${borderColor}` : 'none',
                borderRadius: 7,
                color,
                padding: borderColor ? '13px 18px' : '15px 20px'
              }
            : {}
        }
      >
        {isAIPreview || isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.text}
            linkColor={color}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={backgroundColor}
              color={color}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.text || ''
              }
              style={{ marginBottom: 10 }}
              translationHtml={data.locales[languageVersion].translatable.text}
            >
              <TextEditor
                backgroundColor={backgroundColor}
                color={color}
                content={data.locales[languageVersion].translatable.text}
                linkColor={color}
                onChange={handleChange}
                padding="0"
                placeholder={messages.commonAddText}
              />
            </EditorBlockTranslationReference>
          </>
        )}

        <style jsx>{`
          .content {
            line-height: 1.5;
          }
          .content :global(.vuo-rich-text-editor) {
            font-size: ${textSize === 'larger' ? '1.1em' : '1em'};
          }
        `}</style>
      </div>

      {!isAIPreview && !isPreview && !editorLanguageActive ? (
        <EditorBlockCustomControls>
          <div className="container">
            <EditorBlockTextHighlightControls
              data={data}
              updateData={updateData}
            />
            <EditorBlockConverter
              blockId={blockId}
              blockType="text"
              data={data}
              languageVersion={languageVersion}
            />

            <style jsx>{`
              .container {
                display: flex;
                padding-left: 0;
              }
            `}</style>
          </div>
        </EditorBlockCustomControls>
      ) : null}
    </>
  );
};

export default EditorBlockText;
